import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// ✅ INTERFACE PARA OPCIONES DE NAVEGACIÓN
export interface NavigationOptions {
  replace?: boolean;
  state?: any;
}

// ✅ INTERFACE PARA EL HOOK
export interface UseNavigationReturn {
  navigateTo: (path: string, options?: NavigationOptions) => void;
  goBack: () => void;
  goForward: () => void;
  goToHome: () => void;
  goToLogin: () => void;
  goToRegister: () => void;
  goToSuccess: () => void;
  goToError: (error?: string) => void;
  refresh: () => void;
}

// ✅ HOOK PARA NAVEGACIÓN CONSISTENTE
export const useNavigation = (): UseNavigationReturn => {
  const navigate = useNavigate();

  const navigateTo = useCallback(
    (path: string, options: NavigationOptions = {}) => {
      const { replace = false, state } = options;

      if (replace) {
        navigate(path, { replace: true, state });
      } else {
        navigate(path, { state });
      }
    },
    [navigate]
  );

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const goForward = useCallback(() => {
    navigate(1);
  }, [navigate]);

  const goToHome = useCallback(() => {
    navigateTo('/');
  }, [navigateTo]);

  const goToLogin = useCallback(() => {
    navigateTo('/login');
  }, [navigateTo]);

  const goToRegister = useCallback(() => {
    navigateTo('/register');
  }, [navigateTo]);

  const goToSuccess = useCallback(() => {
    navigateTo('/success', { replace: true });
  }, [navigateTo]);

  const goToError = useCallback(
    (error?: string) => {
      navigateTo('/error', {
        replace: true,
        state: { error },
      });
    },
    [navigateTo]
  );

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  return {
    navigateTo,
    goBack,
    goForward,
    goToHome,
    goToLogin,
    goToRegister,
    goToSuccess,
    goToError,
    refresh,
  };
};

// ✅ HOOK PARA NAVEGACIÓN CON CONFIRMACIÓN
export const useNavigationWithConfirmation = (): UseNavigationReturn & {
  navigateWithConfirmation: (
    path: string,
    message?: string,
    options?: NavigationOptions
  ) => void;
} => {
  const baseNavigation = useNavigation();

  const navigateWithConfirmation = useCallback(
    (
      path: string,
      message = '¿Estás seguro de que quieres salir?',
      options?: NavigationOptions
    ) => {
      if (window.confirm(message)) {
        baseNavigation.navigateTo(path, options);
      }
    },
    [baseNavigation]
  );

  return {
    ...baseNavigation,
    navigateWithConfirmation,
  };
};
